import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogIn, Shield, BookOpen, BarChart3, Heart, Users, Globe, ChevronRight, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import ShikshaLogo from '../components/ShikshaLogo';
import GovLogo from '../components/GovLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_LINKS = [
  { en: 'Ministry of Education', bn: 'শিক্ষা মন্ত্রণালয়', url: 'https://moedu.gov.bd/' },
  { en: 'BANBEIS', bn: 'ব্যানবেইস', url: 'https://banbeis.gov.bd/' },
  { en: 'NCTB', bn: 'এনসিটিবি', url: 'https://nctb.gov.bd/' },
  { en: 'DPE', bn: 'প্রাথমিক শিক্ষা অধিদপ্তর', url: 'https://dpe.gov.bd/' },
  { en: 'DSHE', bn: 'মাধ্যমিক ও উচ্চশিক্ষা অধিদপ্তর', url: 'https://dshe.gov.bd/' },
  { en: 'UGC Bangladesh', bn: 'বিশ্ববিদ্যালয় মঞ্জুরি কমিশন', url: 'https://ugc.gov.bd/' },
];

const STATS = [
  { value: '667K+', en: 'Teachers (Post-Primary)', bn: 'শিক্ষক (মাধ্যমিক+)', icon: Users },
  { value: '64', en: 'Districts', bn: 'জেলা', icon: Globe },
  { value: '37,489', en: 'Post-Primary Institutions', bn: 'মাধ্যমিক+ প্রতিষ্ঠান', icon: BookOpen },
  { value: '17.4M+', en: 'Students (Post-Primary)', bn: 'শিক্ষার্থী (মাধ্যমিক+)', icon: BarChart3 },
];

export default function LoginPage({ onLogin, lang }) {
  const [nid, setNid] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!nid || !pin) { toast.error(lang === 'bn' ? 'এনআইডি ও পিন দিন' : 'Enter NID and PIN'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { nid, pin });
      toast.success(lang === 'bn' ? 'সফলভাবে লগইন হয়েছে' : 'Login successful');
      onLogin(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === 'bn' ? 'এনআইডি বা পিন সঠিক নয়' : 'Invalid NID or PIN'));
    }
    setLoading(false);
  };

  const skipLogin = () => onLogin(null);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
      {/* Government-style top bar */}
      <div style={{ background: '#006A4E', color: 'white', fontSize: '12px', padding: '6px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GovLogo size={24} />
            {lang === 'bn' ? 'গণপ্রজাতন্ত্রী বাংলাদেশ সরকার' : "Government of the People's Republic of Bangladesh"}
          </span>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11} /> +880-2-9513456</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} /> info@shiksha.gov.bd</span>
          </div>
        </div>
      </div>

      {/* Header with logo */}
      <header style={{ background: 'white', borderBottom: '3px solid #F42A41', padding: '16px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <GovLogo size={48} />
            <div style={{ width: 1, height: 36, background: 'var(--color-separator)' }} />
            <ShikshaLogo size={48} showText={true} />
          </div>
          <nav style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <a href="#about" style={{ fontSize: 14, color: 'var(--color-label-secondary)', textDecoration: 'none', fontWeight: 500 }}>
              {lang === 'bn' ? 'সম্পর্কে' : 'About'}
            </a>
            <a href="#features" style={{ fontSize: 14, color: 'var(--color-label-secondary)', textDecoration: 'none', fontWeight: 500 }}>
              {lang === 'bn' ? 'বৈশিষ্ট্য' : 'Features'}
            </a>
            <a href="#contact" style={{ fontSize: 14, color: 'var(--color-label-secondary)', textDecoration: 'none', fontWeight: 500 }}>
              {lang === 'bn' ? 'যোগাযোগ' : 'Contact'}
            </a>
            <button onClick={() => setShowLogin(true)} className="btn btn-sm"
              style={{ background: '#006A4E', color: 'white', fontWeight: 600 }}>
              <LogIn size={14} />
              {lang === 'bn' ? 'লগইন' : 'Login'}
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section — Green banner like banbeis.gov.bd */}
      <section id="about" style={{ background: 'linear-gradient(135deg, #006A4E 0%, #004d38 100%)', color: 'white', padding: '80px 0 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, borderRadius: '50%', background: '#F42A41', opacity: 0.12 }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 200, height: 200, borderRadius: '50%', background: '#F42A41', opacity: 0.08 }} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: 700 }}>
            <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              {lang === 'bn' ? 'শিক্ষা বিডি' : 'Shiksha BD'}
            </h1>
            <p style={{ fontSize: 18, opacity: 0.8, marginTop: 4 }}>
              {lang === 'bn' ? 'শিক্ষক ট্যাবলেট প্ল্যাটফর্ম' : 'Teacher Tablet Platform'}
            </p>
            <p style={{ fontSize: 16, opacity: 0.7, marginTop: 20, lineHeight: 1.7, maxWidth: 560 }}>
              {lang === 'bn'
                ? 'বাংলাদেশ শিক্ষা মন্ত্রণালয়ের অধীনে ৬৬৭,০৯৯ জন শিক্ষক এবং ৩৭,৪৮৯টি প্রতিষ্ঠানের জন্য সমন্বিত শিক্ষা ব্যবস্থাপনা প্ল্যাটফর্ম। উপস্থিতি, খাবার, স্বাস্থ্য, প্রশিক্ষণ ও প্রশাসনিক ড্যাশবোর্ড। (সূত্র: BANBEIS ২০২৪)'
                : 'Integrated education management platform for 667,099 teachers across 37,489 post-primary institutions under the Bangladesh Ministry of Education. Attendance, meals, health, training, and governance dashboards. (Source: BANBEIS 2024)'}
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
              <button onClick={() => setShowLogin(true)} className="btn"
                style={{ background: '#F42A41', color: 'white', fontSize: 16, padding: '14px 28px', fontWeight: 600 }}>
                <LogIn size={18} />
                {lang === 'bn' ? 'লগইন করুন' : 'Login to Platform'}
              </button>
              <button onClick={skipLogin} className="btn"
                style={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 16, padding: '14px 28px', border: '1px solid rgba(255,255,255,0.3)' }}>
                {lang === 'bn' ? 'ডেমো দেখুন' : 'View Demo'}
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ background: 'white', borderBottom: '1px solid var(--color-separator)', padding: '24px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
          {STATS.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.en} style={{ textAlign: 'center' }}>
                <Icon size={24} style={{ color: '#006A4E', margin: '0 auto 8px' }} />
                <div style={{ fontSize: 28, fontWeight: 700, color: '#006A4E' }}>{s.value}</div>
                <div style={{ fontSize: 13, color: 'var(--color-label-secondary)', marginTop: 2 }}>{lang === 'bn' ? s.bn : s.en}</div>
                <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>{lang === 'bn' ? s.en : s.bn}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features section */}
      <section id="features" style={{ padding: '64px 0', background: 'var(--color-background-raised)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, textAlign: 'center', color: 'var(--color-label-primary)', marginBottom: 8 }}>
            {lang === 'bn' ? 'প্ল্যাটফর্ম বৈশিষ্ট্য' : 'Platform Features'}
          </h2>
          <p style={{ fontSize: 12, textAlign: 'center', color: 'var(--color-label-tertiary)', marginBottom: 40 }}>
            {lang === 'bn' ? 'Platform Features' : 'প্ল্যাটফর্ম বৈশিষ্ট্য'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {[
              { icon: BookOpen, en: 'School Management', bn: 'বিদ্যালয় ব্যবস্থাপনা', descEn: 'Attendance tracking, student roster, timetable, and incident reporting', descBn: 'উপস্থিতি ট্র্যাকিং, শিক্ষার্থী তালিকা, সময়সূচি, ও ঘটনা রিপোর্ট' },
              { icon: Shield, en: 'Meal Anti-Fraud', bn: 'খাবার প্রতারণা প্রতিরোধ', descEn: 'Photo evidence, GPS geofencing, beneficiary validation', descBn: 'ছবি প্রমাণ, GPS জিওফেন্সিং, উপকারভোগী যাচাই' },
              { icon: BarChart3, en: 'Governance Dashboard', bn: 'প্রশাসন ড্যাশবোর্ড', descEn: 'KPI scorecards for Principal, District, and MoE levels', descBn: 'প্রধান শিক্ষক, জেলা, ও মন্ত্রণালয় পর্যায়ের KPI স্কোরকার্ড' },
              { icon: Heart, en: 'Swasthya BD Health', bn: 'স্বাস্থ্য বিডি', descEn: 'Private health records, telemedicine, mental wellness tracking', descBn: 'ব্যক্তিগত স্বাস্থ্য রেকর্ড, টেলিমেডিসিন, মানসিক সুস্থতা' },
              { icon: Users, en: 'CPD & LMS', bn: 'প্রশিক্ষণ ও LMS', descEn: 'NAEM-accredited courses, quizzes, certificates, leaderboard', descBn: 'NAEM অনুমোদিত কোর্স, কুইজ, সনদপত্র, লিডারবোর্ড' },
              { icon: Globe, en: 'Gov. Integrations', bn: 'সরকারি সংযোগ', descEn: 'Porichoy NID, BANBEIS, IEDCR, iBAS++, DGHS FHIR', descBn: 'পরিচয় NID, ব্যানবেইস, IEDCR, iBAS++, DGHS FHIR' },
            ].map(f => {
              const Icon = f.icon;
              return (
                <div key={f.en} className="card-surface" style={{ padding: 24 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: '#e5f2ee', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <Icon size={22} style={{ color: '#006A4E' }} />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-label-primary)' }}>{lang === 'bn' ? f.bn : f.en}</h3>
                  <p style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? f.en : f.bn}</p>
                  <p style={{ fontSize: 14, color: 'var(--color-label-secondary)', marginTop: 8, lineHeight: 1.5 }}>{lang === 'bn' ? f.descBn : f.descEn}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Links — government style */}
      <section style={{ padding: '40px 0', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-label-primary)', marginBottom: 16 }}>
            {lang === 'bn' ? 'দ্রুত লিংক' : 'Quick Links'}
            <span style={{ fontSize: 10, color: 'var(--color-label-tertiary)', marginLeft: 8 }}>{lang === 'bn' ? 'Quick Links' : 'দ্রুত লিংক'}</span>
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {QUICK_LINKS.map(link => (
              <a key={link.en} href={link.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--color-background-raised)', borderRadius: 'var(--radius-pill)', fontSize: 13, color: '#006A4E', fontWeight: 500, textDecoration: 'none' }}>
                <ExternalLink size={12} /> {lang === 'bn' ? link.bn : link.en}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" style={{ background: '#006A4E', color: 'white', padding: '40px 0 20px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 40 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <GovLogo size={44} />
                <ShikshaLogo size={36} showText={true} textColor="white" />
              </div>
              <p style={{ fontSize: 13, opacity: 0.7, marginTop: 12, lineHeight: 1.6 }}>
                {lang === 'bn' ? 'বাংলাদেশ শিক্ষা মন্ত্রণালয়ের অধীনে ৬৬৭,০৯৯ শিক্ষক ও ৩৭,৪৮৯ প্রতিষ্ঠানের শিক্ষক ট্যাবলেট প্ল্যাটফর্ম (BANBEIS ২০২৪)' : 'Teacher Tablet Platform for 667,099 teachers across 37,489 institutions under Bangladesh MoE (BANBEIS 2024)'}
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{lang === 'bn' ? 'যোগাযোগ' : 'Contact'}</h4>
              <p style={{ fontSize: 13, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><MapPin size={14} /> {lang === 'bn' ? 'বাংলাদেশ সচিবালয়, ঢাকা-১০০০' : 'Bangladesh Secretariat, Dhaka-1000'}</p>
              <p style={{ fontSize: 13, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><Phone size={14} /> +880-2-9513456</p>
              <p style={{ fontSize: 13, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8 }}><Mail size={14} /> info@shiksha.gov.bd</p>
            </div>
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{lang === 'bn' ? 'অ্যাডমিন প্যানেল' : 'Admin Panel'}</h4>
              <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>{lang === 'bn' ? 'সিস্টেম অ্যাডমিনিস্ট্রেটর? ব্যাকএন্ড প্যানেল ব্যবহার করুন:' : 'System administrator? Access the backend panel:'}</p>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(255,255,255,0.15)', borderRadius: 'var(--radius-pill)', fontSize: 13, color: 'white', fontWeight: 500, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.2)' }}>
                <ExternalLink size={12} /> {lang === 'bn' ? 'API ড্যাশবোর্ড (Swagger)' : 'API Dashboard (Swagger)'}
              </a>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: 32, paddingTop: 16, display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.5 }}>
            <span>&copy; 2026 {lang === 'bn' ? 'শিক্ষা বিডি — সর্বস্বত্ব সংরক্ষিত' : 'Shiksha BD — All Rights Reserved'}</span>
            <span>{lang === 'bn' ? 'SPANXPORT Solutions দ্বারা নির্মিত' : 'Built by SPANXPORT Solutions'}</span>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLogin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogin(false); }}>
          <div className="card-surface animate-slide-up" style={{ width: '100%', maxWidth: 420, padding: 40, margin: 16 }} data-testid="login-modal">
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
                <GovLogo size={52} />
                <ShikshaLogo size={48} showText={true} />
              </div>
              <p style={{ fontSize: 14, color: 'var(--color-label-secondary)', marginTop: 12 }}>
                {lang === 'bn' ? 'আপনার অ্যাকাউন্টে লগইন করুন' : 'Login to your account'}
              </p>
            </div>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 6 }}>
                  {lang === 'bn' ? 'জাতীয় পরিচয়পত্র নম্বর (NID)' : 'National ID Number (NID)'}
                </label>
                <input data-testid="login-nid" type="text" value={nid} onChange={e => setNid(e.target.value)}
                  className="input-filled" placeholder={lang === 'bn' ? 'আপনার ১৩ সংখ্যার NID' : 'Your 13-digit NID'} autoFocus />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 6 }}>
                  {lang === 'bn' ? 'পিন কোড' : 'PIN Code'}
                </label>
                <input data-testid="login-pin" type="password" value={pin} onChange={e => setPin(e.target.value)}
                  className="input-filled" placeholder={lang === 'bn' ? '৬ সংখ্যার পিন' : '6-digit PIN'} />
              </div>
              <button data-testid="login-submit" type="submit" disabled={loading} className="btn"
                style={{ background: '#006A4E', color: 'white', width: '100%', fontSize: 16, padding: 14, marginTop: 8 }}>
                {loading ? (lang === 'bn' ? 'লোড হচ্ছে...' : 'Loading...') : (lang === 'bn' ? 'লগইন করুন' : 'Login')}
              </button>
              <button type="button" onClick={skipLogin} className="btn btn-ghost" style={{ width: '100%', fontSize: 14 }}>
                {lang === 'bn' ? 'ডেমো মোডে প্রবেশ করুন' : 'Enter Demo Mode (Skip Login)'}
              </button>
            </form>
            <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--color-accent-light)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--color-label-secondary)', border: '1px solid var(--color-accent)' }}>
              <p style={{ fontWeight: 700, marginBottom: 6, color: 'var(--color-accent)' }}>{lang === 'bn' ? 'ডেমো অ্যাকাউন্ট — ক্লিক করে লগইন:' : 'Demo Accounts — Click to auto-fill:'}</p>
              {[
                { role: 'Teacher', nid: '1234567890123', pin: '123456' },
                { role: 'Principal', nid: '9876543210987', pin: '654321' },
                { role: 'District Admin', nid: '1111111111111', pin: '111111' },
              ].map(demo => (
                <button key={demo.role} type="button" onClick={() => { setNid(demo.nid); setPin(demo.pin); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: 12, color: 'var(--color-label-primary)' }}>
                  <strong>{demo.role}:</strong> {demo.nid} / {demo.pin}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
